
import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Trophy, Star, Clock, RotateCcw, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { GameResult } from './GameModal';

interface GameResultsProps {
  result: GameResult;
  onPlayAgain: () => void;
  onNextGrid?: () => void;
  onClose: () => void;
}

const GameResults = ({ result, onPlayAgain, onNextGrid, onClose }: GameResultsProps) => {
  const { t } = useTranslation();

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-4 text-center">
      {/* Success Header */}
      <div className="space-y-2">
        <div className="flex justify-center">
          <div className={`
            w-16 h-16 rounded-full flex items-center justify-center
            ${result.isNewBest ? 'bg-gradient-to-r from-yellow-400 to-orange-500' : 'bg-gradient-to-r from-green-400 to-blue-500'}
          `}>
            {result.isNewBest ? <Trophy className="text-white" size={32} /> : <Star className="text-white" size={32} />}
          </div>
        </div>
        
        <h2 className="text-xl font-bold text-gray-800">
          {result.isNewBest ? t('game.newBest') : t('game.completed')}
        </h2>
      </div>

      {/* Game Stats */}
      <Card className="bg-gradient-to-r from-purple-50 to-pink-50">
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="text-center">
              <Star className="w-5 h-5 mx-auto mb-1 text-yellow-500" />
              <div className="font-semibold text-lg">{result.score}</div>
              <div className="text-gray-600">{t('game.score')}</div>
            </div>
            
            <div className="text-center">
              <RotateCcw className="w-5 h-5 mx-auto mb-1 text-blue-500" />
              <div className="font-semibold text-lg">{result.moves}</div>
              <div className="text-gray-600">{t('game.moves')}</div>
            </div>
            
            <div className="text-center">
              <Clock className="w-5 h-5 mx-auto mb-1 text-green-500" />
              <div className="font-semibold text-lg">{formatTime(result.timeInSeconds)}</div>
              <div className="text-gray-600">{t('game.time')}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cultural Fact */}
      {result.unlockedFact && (
        <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
          <CardContent className="p-4">
            <h3 className="font-semibold text-amber-800 mb-2 flex items-center justify-center">
              <Star className="w-4 h-4 mr-2" />
              {t('game.culturalFact')}
            </h3>
            <p className="text-sm text-amber-700">
              {t(`game.facts.${result.unlockedFact}`, { defaultValue: 'Une nouvelle découverte culturelle!' })}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <div className="space-y-3">
        {result.hasNextGrid && onNextGrid && (
          <Button 
            onClick={onNextGrid}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
          >
            <ArrowRight className="w-4 h-4 mr-2" />
            Grille suivante
          </Button>
        )}
        
        <Button 
          onClick={onPlayAgain}
          variant="outline"
          className="w-full"
        >
          {t('game.playAgain')}
        </Button>
        
        <Button 
          onClick={onClose}
          variant="ghost"
          size="sm"
        >
          {t('game.close')}
        </Button>
      </div>
    </div>
  );
};

export default GameResults;
